import { useState } from 'react';
import JsonHighlight from '../shared/JsonHighlight';

interface ResponseViewerProps {
  data: unknown;
  responseTime: number;
  status: number;
  endpoint: string;
  isLoading: boolean;
}

type Tab = 'raw' | 'formatted';

/* ---------- Formatted renderers ---------- */

function TrackRecordView({ data }: { data: unknown }) {
  const items = Array.isArray(data) ? data : [];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {items.map((item: Record<string, unknown>, i: number) => (
        <div key={i} className="bg-ti-bg border border-ti-border rounded-lg p-4 text-center">
          <p className="text-xl font-bold text-ti-gold">{String(item.value || item.metric || '---')}</p>
          <p className="text-xs text-ti-text-muted mt-1">{String(item.context || item.metric || '')}</p>
        </div>
      ))}
    </div>
  );
}

function ExperienceView({ data }: { data: unknown }) {
  const items = Array.isArray(data) ? data : [];
  return (
    <div className="space-y-3">
      {items.map((role: Record<string, unknown>, i: number) => (
        <div key={i} className="relative pl-5 border-l-2 border-ti-blue/30">
          <div className="absolute left-[-5px] top-1.5 w-2 h-2 rounded-full bg-ti-blue" />
          <div className="bg-ti-bg border border-ti-border rounded-lg p-4">
            <div className="flex justify-between items-start gap-2 flex-wrap">
              <div>
                <p className="font-semibold text-ti-text">{String(role.title)}</p>
                <p className="text-sm text-ti-cyan">{String(role.company || role.org)}</p>
              </div>
              <span className="text-xs text-ti-text-dim">{String(role.period || role.dates)}</span>
            </div>
            {role.description ? (
              <p className="text-sm text-ti-text-muted mt-2">{String(role.description)}</p>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function AccomplishmentsView({ data }: { data: unknown }) {
  const items = Array.isArray(data) ? data : [];
  return (
    <div className="space-y-2">
      {items.map((item: Record<string, unknown>, i: number) => (
        <div key={i} className="flex items-start gap-3 bg-ti-bg border border-ti-border rounded-lg p-3">
          <div className="shrink-0 w-1 min-h-[1.5rem] rounded-full bg-ti-gold" />
          <div>
            <p className="text-sm text-ti-text">{String(item.description || item.title || JSON.stringify(item))}</p>
            {(item.value || item.impact) ? (
              <p className="text-xs text-ti-gold font-medium mt-1">{String(item.value || item.impact)}</p>
            ) : null}
            {item.category ? (
              <span className="text-xs text-ti-text-dim">{String(item.category)}</span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function CulturalFitView({ data }: { data: unknown }) {
  const obj = data as Record<string, unknown> | null;
  if (!obj) return null;

  const thriveIn = (obj.thrive_in || obj.thrives_in || []) as string[];
  const notRightFor = (obj.not_right_for || obj.avoid || []) as string[];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-ti-green">Thrives In</h4>
        {thriveIn.map((item: string, i: number) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <svg className="w-4 h-4 text-ti-green shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-ti-text-muted">{item}</span>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-ti-red">Not Right For</h4>
        {notRightFor.map((item: string, i: number) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <svg className="w-4 h-4 text-ti-red shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="text-ti-text-muted">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SkillsView({ data }: { data: unknown }) {
  const items = Array.isArray(data) ? data : [];

  const hasCategories =
    items.length > 0 &&
    typeof items[0] === 'object' &&
    items[0] !== null &&
    ('category' in items[0] || 'group' in items[0]);

  if (hasCategories) {
    return (
      <div className="space-y-3">
        {items.map((group: Record<string, unknown>, i: number) => (
          <div key={i}>
            <h4 className="text-xs font-semibold text-ti-text-dim uppercase tracking-wider mb-1.5">
              {(group.category || group.group) as string}
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {((group.skills || group.items || []) as string[]).map((skill: string, j: number) => (
                <span
                  key={j}
                  className="px-2 py-0.5 bg-ti-purple/10 text-ti-purple text-xs rounded-full border border-ti-purple/20"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((skill: unknown, i: number) => (
        <span
          key={i}
          className="px-2 py-0.5 bg-ti-purple/10 text-ti-purple text-xs rounded-full border border-ti-purple/20"
        >
          {typeof skill === 'string' ? skill : JSON.stringify(skill)}
        </span>
      ))}
    </div>
  );
}

function SeekingView({ data }: { data: unknown }) {
  const obj = data as Record<string, unknown> | null;
  if (!obj) return null;

  return (
    <div className="space-y-4">
      {Array.isArray(obj.roles) && (
        <div>
          <h4 className="text-sm font-semibold text-ti-text mb-2">Target Roles</h4>
          <div className="flex flex-wrap gap-2">
            {(obj.roles as string[]).map((role: string, i: number) => (
              <span key={i} className="px-3 py-1 bg-ti-blue/10 text-ti-blue text-sm rounded-lg border border-ti-blue/20">
                {role}
              </span>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(obj.org_types) && (
        <div>
          <h4 className="text-sm font-semibold text-ti-text mb-2">Ideal Organizations</h4>
          <div className="flex flex-wrap gap-2">
            {(obj.org_types as string[]).map((org: string, i: number) => (
              <span key={i} className="px-3 py-1 bg-ti-cyan/10 text-ti-cyan text-sm rounded-lg border border-ti-cyan/20">
                {org}
              </span>
            ))}
          </div>
        </div>
      )}
      {obj.location ? (
        <div>
          <h4 className="text-sm font-semibold text-ti-text mb-1">Location</h4>
          <p className="text-sm text-ti-text-muted">{String(obj.location)}</p>
        </div>
      ) : null}
      {obj.availability ? (
        <div>
          <h4 className="text-sm font-semibold text-ti-text mb-1">Availability</h4>
          <p className="text-sm text-ti-text-muted">{String(obj.availability)}</p>
        </div>
      ) : null}
      {/* Render any other top-level keys */}
      {Object.entries(obj)
        .filter(([k]) => !['roles', 'org_types', 'location', 'availability'].includes(k))
        .map(([key, value]) => (
          <div key={key}>
            <h4 className="text-sm font-semibold text-ti-text mb-1 capitalize">
              {key.replace(/_/g, ' ')}
            </h4>
            <p className="text-sm text-ti-text-muted">
              {typeof value === 'string' ? value : JSON.stringify(value)}
            </p>
          </div>
        ))}
    </div>
  );
}

/* ---------- Formatted dispatch ---------- */

function FormattedView({ data, endpoint }: { data: unknown; endpoint: string }) {
  // Extract the data payload -- the API wraps in { data, meta }
  const payload = (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>))
    ? (data as Record<string, unknown>).data
    : data;

  if (endpoint === '/track-record') return <TrackRecordView data={payload} />;
  if (endpoint === '/experience') return <ExperienceView data={payload} />;
  if (endpoint === '/accomplishments') return <AccomplishmentsView data={payload} />;
  if (endpoint === '/cultural-fit') return <CulturalFitView data={payload} />;
  if (endpoint === '/skills') return <SkillsView data={payload} />;
  if (endpoint === '/seeking') return <SeekingView data={payload} />;

  // Default: show JSON
  return <JsonHighlight data={data} maxHeight="500px" />;
}

/* ---------- Loading skeleton ---------- */

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <div className="h-4 bg-ti-border rounded w-24" />
          <div className="h-4 bg-ti-border rounded flex-1" style={{ maxWidth: `${50 + Math.random() * 40}%` }} />
        </div>
      ))}
    </div>
  );
}

/* ---------- Main component ---------- */

export default function ResponseViewer({
  data,
  responseTime,
  status,
  endpoint,
  isLoading,
}: ResponseViewerProps) {
  const [activeTab, setActiveTab] = useState<Tab>('raw');

  const statusColor = status >= 200 && status < 300 ? 'text-ti-green' : 'text-ti-red';
  const statusText = status >= 200 && status < 300 ? 'OK' : 'Error';

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-ti-border bg-ti-surface">
        <button
          onClick={() => setActiveTab('raw')}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${
            activeTab === 'raw'
              ? 'bg-ti-blue/15 text-ti-blue'
              : 'text-ti-text-dim hover:text-ti-text-muted'
          }`}
        >
          Raw JSON
        </button>
        <button
          onClick={() => setActiveTab('formatted')}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${
            activeTab === 'formatted'
              ? 'bg-ti-blue/15 text-ti-blue'
              : 'text-ti-text-dim hover:text-ti-text-muted'
          }`}
        >
          Formatted
        </button>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto custom-scrollbar p-1">
        {isLoading ? (
          <LoadingSkeleton />
        ) : data === null ? (
          <div className="flex items-center justify-center h-full text-ti-text-dim text-sm">
            Select an endpoint to see the response
          </div>
        ) : activeTab === 'raw' ? (
          <JsonHighlight data={data} maxHeight="100%" />
        ) : (
          <div className="p-4">
            <FormattedView data={data} endpoint={endpoint} />
          </div>
        )}
      </div>

      {/* Status bar */}
      {data !== null && !isLoading && (
        <div className="flex items-center gap-4 px-3 py-1.5 border-t border-ti-border bg-ti-surface text-xs font-mono">
          <span className={statusColor}>
            {status} {statusText}
          </span>
          <span className="text-ti-text-dim">|</span>
          <span className="text-ti-text-muted">{responseTime}ms</span>
          <span className="text-ti-text-dim">|</span>
          <span className="text-ti-text-muted">application/json</span>
        </div>
      )}
    </div>
  );
}
